import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { SIGNUP_URL, ORIGINAL_MARKETS_URL } from "./links";

const HERO_BG =
  "https://images.unsplash.com/photo-1647101734210-ff5d79813069?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxkYXJrJTIwYWJzdHJhY3QlMjBnb2xkfGVufDB8fHx8MTc4NzY0MTMyOHww&ixlib=rb-4.1.0&q=85";

const MaskedLine = ({ children, delay, className }) => (
  <span className="block overflow-hidden">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen flex-col justify-end overflow-hidden"
    >
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img
          src={HERO_BG}
          alt=""
          className="h-[120%] w-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-black/70" />
        <div className="absolute inset-x-0 bottom-0 h-64 bg-[#050505] [mask-image:linear-gradient(to_top,black,transparent)]" />
      </motion.div>

      <motion.svg
        viewBox="0 0 1440 800"
        preserveAspectRatio="none"
        className="pointer-events-none absolute inset-0 h-full w-full opacity-40 mix-blend-screen"
        style={{ transform: "translateY(48px)" }}
        aria-hidden="true"
      >
        <motion.path
          d="M0 600 L90 575 L180 612 L270 538 L360 566 L450 498 L540 532 L630 468 L720 506 L810 428 L900 472 L990 398 L1080 446 L1170 378 L1260 416 L1350 348 L1440 386"
          fill="none"
          stroke="#D4AF37"
          strokeWidth="2"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 2.6, delay: 1.2, ease: "easeInOut" }}
          style={{ filter: "drop-shadow(0 0 14px rgba(212, 175, 55, 0.55))" }}
        />
        <motion.path
          d="M0 660 L120 640 L240 668 L360 618 L480 646 L600 596 L720 624 L840 572 L960 606 L1080 552 L1200 584 L1320 534 L1440 560"
          fill="none"
          stroke="#F3E5AB"
          strokeWidth="1"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 3.2, delay: 1.6, ease: "easeInOut" }}
          style={{ filter: "drop-shadow(0 0 8px rgba(243, 229, 171, 0.3))" }}
          opacity="0.35"
        />
      </motion.svg>

      <motion.div
        style={{ y: contentY, opacity: fade }}
        className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-16 pt-40 lg:px-10"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.4 }}
          className="mb-8 flex items-center gap-4"
        >
          <span className="text-[10px] uppercase tracking-[0.3em] text-gold" data-testid="hero-overline">
            N° 001 · Private Terminal
          </span>
          <span className="h-px w-16 bg-gold/40" />
          <span className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white/50">
            <span className="h-1.5 w-1.5 rounded-full bg-gold animate-pulse-dot" />
            Live · Markets open
          </span>
        </motion.div>

        <a
          data-testid="hero-brand-link"
          href={ORIGINAL_MARKETS_URL}
          aria-label="Aureon Global Markets"
          className="block"
        >
          <h1 className="font-serif text-[16vw] font-light leading-[0.9] tracking-tighter text-white sm:text-[13vw] lg:text-[10rem]">
            <MaskedLine delay={0.55}>AUREON</MaskedLine>
            <MaskedLine delay={0.7}>GLOBAL</MaskedLine>
            <MaskedLine delay={0.85} className="italic text-gold">
              MARKETS
            </MaskedLine>
          </h1>
        </a>

        <div className="mt-12 flex flex-col gap-10 md:flex-row md:items-end md:justify-between">
          <motion.p
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-md text-sm leading-relaxed text-white/60 [text-shadow:0_1px_10px_rgba(0,0,0,0.9)]"
          >
            A private, simulated trading terminal — practise the top 25 crypto
            assets, mega-cap equities, precious metals and global forex on live
            market data, inside your own Aureon ID training environment.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.25, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-wrap items-center gap-4"
          >
            <a
              data-testid="hero-markets-btn"
              href="/terminal"
              className="group flex items-center gap-3 rounded-full bg-gold px-8 py-4 text-xs uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-gold-light"
            >
              Open the simulator
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              data-testid="hero-signup-btn"
              href={SIGNUP_URL}
              className="rounded-full border border-white/20 px-8 py-4 text-xs uppercase tracking-[0.2em] text-white/80 transition-colors duration-300 hover:border-gold hover:text-gold"
            >
              Create account
            </a>
            <a
              data-testid="hero-access-btn"
              href="#access"
              className="group flex items-center gap-2 px-2 py-4 text-xs uppercase tracking-[0.2em] text-gold/80 transition-colors duration-300 hover:text-gold"
            >
              See training scenarios
              <span className="transition-transform duration-300 group-hover:translate-y-0.5">↓</span>
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 1.6 }}
          className="mt-16 grid grid-cols-2 gap-6 border-t border-white/10 pt-8 text-[10px] uppercase tracking-[0.25em] text-white/40 md:grid-cols-4"
        >
          <span data-testid="hero-stat-assets">4 asset classes</span>
          <span data-testid="hero-stat-feed">Live market data</span>
          <span data-testid="hero-stat-vault">Simulated book</span>
          <span data-testid="hero-stat-id">Aureon ID · Private</span>
        </motion.div>
      </motion.div>
    </section>
  );
}
